import Vue from 'vue'
import App from './App.vue'
import {store} from './store/store'
import router from './router'
import logger from './utils/logger'

// Initialize theme system BEFORE loading CSS
// This sets window variables and CSS custom properties based on VUE_APP_THEME
import { initializeTheme } from './themes'
initializeTheme()

// Import modern CSS framework
import './assets/css/tailwind.css'
import './assets/css/theme.css'

import GlobalEvents from 'vue-global-events'
Vue.component('GlobalEvents', GlobalEvents)

// Accessible dialog focus-trap directive (focus trap + return-focus; optional Esc)
import focusTrap from './directives/focusTrap'
Vue.directive('focus-trap', focusTrap)

// Import datamethods globally
import datamethods from './services/datamethods'
Vue.mixin(datamethods)

// Import toast service
import ToastPlugin from './services/toastService'
Vue.use(ToastPlugin)

// Import error handler mixin for component-level error handling
import { errorHandlerMixin } from './services/errorHandler'
Vue.mixin(errorHandlerMixin)

Vue.config.productionTip = false

// ReCaptcha disabled for testing
// import {VueReCaptcha} from 'vue-recaptcha-v3'
// Vue.use(VueReCaptcha, { siteKey: '6LfnKzMfAAAAABMt1ZO3dHdXHyJv45GMPTgaDt2X' })

// CRITICAL FIX: Wait for config to load before initializing Vue app
// This ensures window.VUE_CONFIG_API_ECOMMERCE is set before ecommerceApi.js runs
function initializeApp() {
  if (window.ECOMMERCE_CONFIG_LOADED) {
    logger.debug('🚀 Initializing Vue app with API URL:', window.VUE_CONFIG_API_ECOMMERCE || 'fallback');

    // CRITICAL: Update axios baseURL after config loads
    // The axios instances were created before config loaded, so we need to update them
    const { ECommerceAPI } = require('./services/ecommerceApi')
    const { HTTP, HTTPImage } = require('./services/repository')

    if (window.VUE_CONFIG_API_ECOMMERCE) {
      ECommerceAPI.defaults.baseURL = window.VUE_CONFIG_API_ECOMMERCE
      logger.debug('✅ Updated ECommerceAPI baseURL to:', ECommerceAPI.defaults.baseURL)
    }

    // CRITICAL: Also update HTTP client used by datamethods.js for login/wallet APIs
    if (window.VUE_CONFIG_API) {
      HTTP.defaults.baseURL = window.VUE_CONFIG_API
      HTTPImage.defaults.baseURL = window.VUE_CONFIG_API_IMAGE || window.VUE_CONFIG_API
      logger.debug('✅ Updated HTTP baseURL to:', HTTP.defaults.baseURL)
    }

    // Initialize Sentry BEFORE mount so mount-time errors are captured. No-op unless the
    // tenant config set window.SENTRY_DSN. Exposes window.Sentry for errorHandler.js too.
    const { initSentry } = require('./utils/sentry')
    if (initSentry(Vue)) {
      logger.debug('🛡️ Sentry error tracking active')
    }

    // Setup centralized error handling with Axios interceptors
    const { setupErrorHandling } = require('./services/errorHandler')
    setupErrorHandling(store)

    new Vue({
      store,
      router,
      render: function (h) { return h(App) },
    }).$mount('#app')
  } else {
    // Config not loaded yet, wait 50ms and try again
    setTimeout(initializeApp, 50)
  }
}

// Start initialization
initializeApp()

// Unregister service workers on localhost to prevent dev loops
if ('serviceWorker' in navigator && window.location.hostname === 'localhost') {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister()
      logger.debug('[PWA] Unregistered service worker for localhost development')
    })
  })
}

// Register service worker for PWA support (disabled on localhost to prevent dev loops)
if ('serviceWorker' in navigator && window.location.hostname !== 'localhost') {
  // Auto-reload when new service worker takes control (ensures fresh assets)
  let refreshing = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (refreshing) return
    refreshing = true
    logger.debug('[PWA] New service worker activated, reloading for fresh assets...')
    window.location.reload()
  })

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .then((registration) => {
        logger.debug('[PWA] Service Worker registered:', registration.scope)

        // Check for updates on page load AND periodically. update() can transiently reject (network
        // blip, edge 5xx) — the browser surfaces it as "Failed to update a ServiceWorker", and without
        // a .catch() that became an UNHANDLED rejection flooding Sentry (2026-08-25). Swallow it: the SW
        // keeps serving from cache and the next check retries. 15 min (was 5 — excessive) still stays fresh.
        registration.update().catch(() => {})
        setInterval(() => {
          registration.update().catch(() => {})
        }, 1000 * 60 * 15)

        // Handle service worker updates
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          logger.debug('[PWA] New service worker installing...')

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              logger.debug('[PWA] New service worker installed, activating immediately...')
              // Tell the new SW to skip waiting and take control immediately
              newWorker.postMessage({ type: 'SKIP_WAITING' })
            }
          })
        })
      })
      .catch((error) => {
        logger.error('[PWA] Service Worker registration failed:', error)
      })
  })
}
